const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type TrapFocusOptions = {
  /** Called when Escape is pressed inside the trap. */
  onEscape?: () => void;
  /** Locks background scroll for the lifetime of the trap. Defaults to true. */
  lockBodyScroll?: boolean;
  /**
   * "deferred" (default) moves focus in a setTimeout(0), after Svelte's own DOM updates settle.
   * "sync" moves focus immediately, before returning from the action — needed by callers whose
   * tests assert focus landed synchronously right after the dialog mounts.
   */
  focusTiming?: "sync" | "deferred";
};

/**
 * Shared focus-trap action for custom (non-<dialog>) overlays: traps Tab within the node,
 * handles Escape, and restores focus to whatever was focused before the overlay opened.
 * Native <dialog>-based overlays (SignInDialog, OtherInterestModal) get this for free from
 * showModal() and don't need it — this is for the ones that render a plain positioned <div>.
 */
export function trapFocus(node: HTMLElement, options: TrapFocusOptions = {}) {
  let { onEscape, lockBodyScroll = true } = options;
  const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const previousBodyOverflow = document.body.style.overflow;
  if (lockBodyScroll) document.body.style.overflow = "hidden";

  const focusable = () =>
    Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) => !element.hasAttribute("hidden") && (element.offsetParent !== null || element === document.activeElement)
    );

  const initialFocusTarget = node.querySelector<HTMLElement>('[data-autofocus="true"]') ?? focusable()[0] ?? node;
  if (options.focusTiming === "sync") {
    initialFocusTarget?.focus();
  } else {
    window.setTimeout(() => initialFocusTarget?.focus(), 0);
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onEscape?.();
      return;
    }
    if (event.key !== "Tab") return;

    const items = focusable();
    if (items.length === 0) {
      event.preventDefault();
      node.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !node.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !node.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  node.addEventListener("keydown", handleKeydown);

  return {
    update(nextOptions: TrapFocusOptions = {}) {
      onEscape = nextOptions.onEscape;
      lockBodyScroll = nextOptions.lockBodyScroll ?? true;
    },
    destroy() {
      node.removeEventListener("keydown", handleKeydown);
      if (lockBodyScroll) document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus?.();
    },
  };
}
